import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Upload, X, Sparkles, ArrowRight, ScanEye, Target, Users,
  FlaskConical, Swords, TrendingUp, Map, Briefcase, Bookmark, Lightbulb,
  AlertTriangle, CheckCircle2, RefreshCw, Loader2, Lock, Check, Globe,
} from "lucide-react";
import { AIResourcesShell, SidebarPromo } from "../../components/ai/AIResourcesShell";
import { useAIResources } from "../../hooks/useAIResources";
import { useAuth } from "../../hooks/useAuth";
import { usePro } from "../../hooks/usePro";
import { aiOpportunityScan, aiSave, saveScanHistory } from "../../lib/api";

const BUILD_TYPES = ["SaaS", "Mobile App", "Marketplace", "AI Business", "Service Business", "Subscription", "Local Business", "Something Else"];
const GOALS = ["Make Money", "Save Time", "Solve a Problem", "Automate", "Passive Income", "Recurring Revenue"];
const AUDIENCES = ["Consumers", "Businesses", "Students", "Restaurants", "Hotels", "Healthcare", "Everyone"];

const STEPS = [
  { n: 1, label: "Capture", sub: "Take a photo or upload", icon: Camera },
  { n: 2, label: "Tell Us More", sub: "Answer a few questions", icon: Target },
  { n: 3, label: "AI Analyzes", sub: "We find opportunities", icon: Sparkles },
  { n: 4, label: "Get Ideas", sub: "Receive your report", icon: Lightbulb },
];

const ANALYSIS_LINES = ["Analyzing image content", "Detecting problems", "Generating startup ideas", "Finalizing opportunity report"];

const HIDDEN_ICONS = {
  Marketplace: Swords, Subscription: RefreshCw, AI: Sparkles, Automation: Target,
  B2B: Briefcase, "Local Business": Map,
};
const hiddenIcon = (type) => {
  const key = Object.keys(HIDDEN_ICONS).find((k) => (type || "").toLowerCase().includes(k.toLowerCase()));
  return HIDDEN_ICONS[key] || Lightbulb;
};

const diffClass = (d) => {
  const m = { Easy: "bg-green-50 text-green-600", Medium: "bg-amber-50 text-amber-600", Hard: "bg-red-50 text-red-500" };
  return m[d] || m.Medium;
};

const OptionPills = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => {
      const on = value === o;
      return (
        <button
          key={o}
          onClick={() => onChange(o)}
          data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`text-sm font-semibold px-3.5 py-2 rounded-full border transition-colors ${
            on ? "bg-[#D4AF37] text-[#0F0F0F] border-[#D4AF37]" : "bg-white text-[#374151] border-gray-200 hover:border-[#D4AF37]/50"
          }`}
        >
          {o}
        </button>
      );
    })}
  </div>
);

const ScoreBadge = ({ score }) => (
  <div
    className="w-14 h-14 shrink-0 rounded-2xl bg-[#0F0F0F] text-[#D4AF37] flex flex-col items-center justify-center leading-none shadow-sm"
    data-testid="idea-score-badge"
  >
    <span className="text-xl font-extrabold">{score}</span>
    <span className="text-[9px] text-[#c9c39f] font-semibold mt-0.5">/100</span>
  </div>
);

export default function OpportunityScanner() {
  const navigate = useNavigate();
  const { requireAuth, user, promptLogin, loading } = useAuth();
  const { isPro, openUpgrade } = usePro();

  // Non-Pro founders get 1 FREE scan, then the Pro gate kicks in.
  const FREE_SCANS = 1;
  const [scanUsed, setScanUsed] = useState(() => {
    try { return parseInt(localStorage.getItem("lbs_scanner_tries") || "0", 10) || 0; } catch { return 0; }
  });
  const canScan = isPro || scanUsed < FREE_SCANS;
  const {
    scanner, patchScanner,
    patchValidation, patchCompetitor, patchMarket,
  } = useAIResources();

  // Gate: the AI Opportunity Scanner is a LaunchBS Pro feature. Logged-out users
  // get the "Continue with Google" card, then the subscription modal after sign-in.
  const openGate = useCallback(() => {
    if (!user) promptLogin("scanner");
    else openUpgrade("scanner");
  }, [user, promptLogin, openUpgrade]);

  const gatedRef = useRef(false);
  useEffect(() => {
    if (loading || canScan) return;
    if (!gatedRef.current) {
      gatedRef.current = true;
      openGate();
    }
  }, [loading, canScan, openGate]);

  const { image, buildType, goal, audience, result } = scanner;
  const [phase, setPhase] = useState(result ? "results" : "capture"); // capture | analyzing | results
  const [analysisIdx, setAnalysisIdx] = useState(0);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    if (phase !== "analyzing") return;
    setAnalysisIdx(0);
    const t = setInterval(() => setAnalysisIdx((i) => Math.min(i + 1, ANALYSIS_LINES.length - 1)), 900);
    return () => clearInterval(t);
  }, [phase]);

  const handleFile = (file) => {
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|webp)/.test(file.type)) {
      toast.error("Please use a JPG, PNG or WEBP image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        let { width, height } = img;
        if (width > max || height > max) {
          const r = Math.min(max / width, max / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        patchScanner({ image: dataUrl });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!image) { toast.error("Add a photo first."); return; }
    setPhase("analyzing");
    try {
      const [res] = await Promise.all([
        aiOpportunityScan({ image_base64: image, build_type: buildType, goal, audience }),
        new Promise((r) => setTimeout(r, 3200)),
      ]);
      patchScanner({ result: res });
      setPhase("results");
      // Consume the free scan for non-Pro founders.
      if (!isPro) {
        const n = scanUsed + 1;
        setScanUsed(n);
        try { localStorage.setItem("lbs_scanner_tries", String(n)); } catch { /* ignore */ }
      }
      // Auto-save every scan to History (logged-in founders only; best-effort).
      if (user) {
        const ideas = (res.ideas || []).map((it) => ({
          name: it.name, description: it.description, ai_score: it.ai_score,
          revenue_model: it.revenue_model, difficulty: it.difficulty,
          audience: audience || "", problem: (res.problems || [])[0] || "",
        }));
        saveScanHistory({ label: (res.sees || buildType || "Scan").slice(0, 80), photo_ref: image, ideas }).catch(() => {});
      }
      setTimeout(() => document.getElementById("scan-report")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } catch {
      toast.error("Could not analyze the image. Please try again.");
      setPhase("capture");
    }
  };

  const rescan = () => {
    if (!canScan) { openGate(); return; }
    patchScanner({ result: null });
    setPhase("capture");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- Connect an idea into other LaunchBS tools (pre-fill, no auto-run) ----
  const toValidate = (idea) => {
    patchValidation({
      form: {
        name: idea.name,
        does: idea.description,
        customer: audience || "",
        problem: (result?.problems || [])[0] || "",
        different: "",
      },
      report: null,
    });
    toast.success("Idea sent to Validation Lab");
    navigate("/ai-resources/validation-lab");
  };
  const toMarket = (idea) => {
    patchMarket({ idea: `${idea.name} — ${idea.description}`, insights: null });
    toast.success("Idea sent to Market Size Calculator");
    navigate("/ai-resources/market-size");
  };
  const toCompetitors = (idea) => {
    patchCompetitor({ idea: `${idea.name}: ${idea.description}`, result: null });
    toast.success("Idea sent to Competitor Search");
    navigate("/ai-resources/competitor-search");
  };
  const toBlueprint = (idea) => {
    window.dispatchEvent(new CustomEvent("lbs:open-blueprint", {
      detail: {
        mode: "scanner_idea",
        idea: {
          name: idea.name,
          description: idea.description,
          problem: (result?.problems || [])[0] || "",
          audience: audience || "",
          build_type: buildType || "",
          revenue_model: idea.revenue_model || "",
        },
      },
    }));
  };
  const saveIdea = (idea) => {
    requireAuth(async () => {
      try {
        await aiSave("idea", { ...idea, audience, goal, build_type: buildType, sees: result?.sees });
        toast.success("Saved to your Notebook");
      } catch { toast.error("Could not save. Try again."); }
    });
  };

  const IDEA_ACTIONS = [
    { key: "validate", label: "Validate Idea", icon: FlaskConical, fn: toValidate },
    { key: "blueprint", label: "Generate Blueprint", icon: Sparkles, fn: toBlueprint },
    { key: "market", label: "Research Market", icon: TrendingUp, fn: toMarket },
    { key: "competitors", label: "Find Competitors", icon: Swords, fn: toCompetitors },
    { key: "save", label: "Save Idea", icon: Bookmark, fn: saveIdea },
  ];

  return (
    <AIResourcesShell
      active="opportunity-scanner"
      title="AI Opportunity Scanner"
      subtitle="Turn anything you see into your next startup idea using AI."
      icon={ScanEye}
      extra={
        <div className="space-y-5">
          <SidebarPromo
            title="Snap → Startup"
            desc="Go from photo to a validated, build-ready idea in a few clicks."
          />
          <div className="rounded-2xl border border-gray-100 p-5 bg-white">
            <p className="text-xs font-bold uppercase tracking-widest text-[#9aa1ab]">Photos stay private</p>
            <p className="text-sm text-[#687280] mt-2 leading-snug">Images are used only to generate ideas and are never shared.</p>
          </div>
        </div>
      }
    >
      {/* Pro gate — locked view once the free scan is used (non-Pro) */}
      {!canScan && phase !== "results" && (
        <div className="max-w-2xl mx-auto" data-testid="scanner-gate">
          <div className="rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-8 md:p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(circle at 50% 15%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 55%)" }} />
            <div className="relative">
              <span className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 text-[#D4AF37] flex items-center justify-center mx-auto"><ScanEye size={26} /></span>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold mt-5">AI Opportunity Scanner</h2>
              <p className="text-[#c9c39f] mt-2">Turn anything you see into your next startup.</p>
              <ul className="mt-6 space-y-2.5 text-left max-w-xs mx-auto">
                {["Scan real-world problems", "Discover hidden opportunities", "Generate startup ideas in seconds"].map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm">
                    <span className="w-5 h-5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center shrink-0"><Check size={12} strokeWidth={3} /></span>
                    {p}
                  </li>
                ))}
              </ul>
              <button
                onClick={openGate}
                className="mt-7 w-full max-w-xs mx-auto inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors"
                data-testid="scanner-gate-cta"
              >
                {user ? <><Lock size={15} /> Unlock with Pro</> : <><Sparkles size={15} /> Continue with Google</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {(canScan || phase === "results") && (<>
      {/* BETA + stepper */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[9px] font-extrabold bg-[#D4AF37] text-[#0F0F0F] px-2 py-0.5 rounded-full">BETA</span>
        <span className="text-xs text-[#9aa1ab] font-semibold">Point your camera at anything — AI does the rest.</span>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="scanner-stepper">
        {STEPS.map((s, i) => {
          const active = (phase === "capture" && i < 2) || (phase === "analyzing" && s.n === 3) || (phase === "results" && s.n === 4);
          return (
            <div key={s.n} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${active ? "bg-[#FBF7EA]" : ""}`}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${active ? "bg-[#D4AF37] text-[#0F0F0F]" : "bg-[#0F0F0F] text-white"}`}>{s.n}</span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-[#0F0F0F] truncate">{s.label}</p>
                <p className="text-[11px] text-[#9aa1ab] truncate">{s.sub}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* CAPTURE + QUESTIONS */}
      {phase === "capture" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Capture */}
          <div className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="scanner-capture">
            <h3 className="font-display text-lg font-bold text-[#0F0F0F]">1. Capture Anything</h3>
            <p className="text-sm text-[#687280] mt-1">Take a photo or upload an image of anything around you.</p>

            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="scanner-file-input" />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="scanner-camera-input" />

            {image ? (
              <div className="mt-4 relative rounded-2xl overflow-hidden border border-gray-100">
                <img src={image} alt="Captured" className="w-full h-64 object-cover" data-testid="scanner-preview" />
                <button onClick={() => patchScanner({ image: null })} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" data-testid="scanner-remove-image"><X size={16} /></button>
              </div>
            ) : (
              <div
                onClick={() => fileRef.current?.click()}
                className="mt-4 h-64 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-[#D4AF37]/60 hover:bg-[#FBF7EA]/40 transition-colors"
                data-testid="scanner-dropzone"
              >
                <span className="w-12 h-12 rounded-2xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center"><Camera size={22} /></span>
                <p className="text-sm font-semibold text-[#374151]">Tap to add a photo</p>
                <p className="text-xs text-[#9aa1ab]">JPG, PNG or WEBP</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-4">
              <button onClick={() => cameraRef.current?.click()} className="inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold bg-[#0F0F0F] text-white hover:bg-black transition-colors" data-testid="scanner-take-photo">
                <Camera size={16} /> Take Photo
              </button>
              <button onClick={() => fileRef.current?.click()} className="inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold border border-gray-200 text-[#374151] hover:border-[#D4AF37]/60 transition-colors" data-testid="scanner-upload">
                <Upload size={16} /> Upload Image
              </button>
            </div>
            <p className="text-[11px] text-[#9aa1ab] mt-3 text-center">Photos stay private and are used only to generate ideas.</p>
          </div>

          {/* Questions */}
          <div className="rounded-2xl border border-gray-100 bg-white p-6 space-y-5" data-testid="scanner-questions">
            <div>
              <h3 className="font-display text-lg font-bold text-[#0F0F0F]">2. Tell Us More</h3>
              <p className="text-sm text-[#687280] mt-1">Help our AI understand what you're looking to build.</p>
            </div>
            <div>
              <p className="text-sm font-bold text-[#0F0F0F] mb-2 flex items-center gap-1.5"><Target size={15} className="text-[#D4AF37]" /> What do you want to build?</p>
              <OptionPills options={BUILD_TYPES} value={buildType} onChange={(v) => patchScanner({ buildType: v })} testid="scanner-q-build" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#0F0F0F] mb-2 flex items-center gap-1.5"><Sparkles size={15} className="text-[#D4AF37]" /> What is your goal?</p>
              <OptionPills options={GOALS} value={goal} onChange={(v) => patchScanner({ goal: v })} testid="scanner-q-goal" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#0F0F0F] mb-2 flex items-center gap-1.5"><Users size={15} className="text-[#D4AF37]" /> Who is it for?</p>
              <OptionPills options={AUDIENCES} value={audience} onChange={(v) => patchScanner({ audience: v })} testid="scanner-q-audience" />
            </div>
            <button
              onClick={generate}
              disabled={!image}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold transition-colors ${
                image ? "bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030]" : "bg-gray-100 text-[#9aa1ab] cursor-not-allowed"
              }`}
              data-testid="scanner-generate-btn"
            >
              Generate Ideas <Sparkles size={16} />
            </button>
            {!image && <p className="text-[11px] text-center text-[#9aa1ab] -mt-2">Add a photo to unlock idea generation.</p>}
          </div>
        </div>
      )}

      {/* ANALYZING */}
      {phase === "analyzing" && (
        <div className="rounded-2xl border border-gray-100 bg-white p-10 flex flex-col items-center text-center" data-testid="scanner-analyzing">
          <div className="relative w-24 h-24 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border-4 border-[#D4AF37]/25 border-t-[#D4AF37] animate-spin" />
            <ScanEye size={30} className="text-[#D4AF37]" />
          </div>
          <h3 className="font-display text-xl font-extrabold text-[#0F0F0F] mt-6">AI Analysis in Progress</h3>
          <p className="text-sm text-[#687280] mt-1">Our AI is analyzing your image and finding opportunities…</p>
          <div className="mt-6 space-y-2.5 w-full max-w-xs mx-auto text-left">
            {ANALYSIS_LINES.map((l, i) => (
              <div key={l} className="flex items-center gap-2.5 text-sm">
                {i < analysisIdx ? <CheckCircle2 size={17} className="text-green-500" /> : i === analysisIdx ? <Loader2 size={17} className="text-[#D4AF37] animate-spin" /> : <span className="w-[17px] h-[17px] rounded-full border border-gray-200" />}
                <span className={i <= analysisIdx ? "text-[#0F0F0F] font-semibold" : "text-[#9aa1ab]"}>{l}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#9aa1ab] mt-6">This usually takes 20–40 seconds.</p>
        </div>
      )}

      {/* RESULTS */}
      {phase === "results" && result && (
        <div id="scan-report" data-testid="scan-report" className="space-y-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#D4AF37]">Opportunity Report</p>
              <h3 className="font-display text-2xl font-extrabold text-[#0F0F0F]">We found {result.ideas.length} ideas for you</h3>
            </div>
            <button onClick={rescan} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold border border-gray-200 text-[#374151] hover:border-[#D4AF37]/60 transition-colors shrink-0" data-testid="scanner-rescan-btn">
              <RefreshCw size={15} /> Scan Again
            </button>
          </div>

          {/* AI Sees + Problems */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
            <div className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="scanner-sees">
              <p className="text-xs font-bold uppercase tracking-widest text-[#9aa1ab] flex items-center gap-1.5"><ScanEye size={14} className="text-[#D4AF37]" /> What AI Sees</p>
              <p className="text-[#0F0F0F] mt-3 leading-relaxed">{result.sees}</p>
              {image && <img src={image} alt="Scanned" className="w-full h-40 object-cover rounded-xl mt-4 border border-gray-100" />}
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="scanner-problems">
              <p className="text-xs font-bold uppercase tracking-widest text-[#9aa1ab] flex items-center gap-1.5"><AlertTriangle size={14} className="text-[#D4AF37]" /> Problems Found</p>
              <ul className="mt-3 space-y-2">
                {result.problems.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#374151]">
                    <AlertTriangle size={15} className="text-amber-500 mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Startup Ideas */}
          <div>
            <h4 className="font-display text-lg font-bold text-[#0F0F0F] mb-3">Top Startup Ideas For You</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="scanner-ideas">
              {result.ideas.map((idea, i) => (
                <div key={i} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_16px_44px_rgb(0,0,0,0.08)] transition-all" data-testid={`scanner-idea-${i}`}>
                  <div className="flex items-start gap-4">
                    <ScoreBadge score={idea.ai_score} />
                    <div className="min-w-0 flex-1">
                      <h5 className="font-display text-base font-bold text-[#0F0F0F]">{idea.name}</h5>
                      <p className="text-sm text-[#687280] mt-1 leading-snug">{idea.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#FBF7EA] text-[#8a6d1a]">{idea.revenue_model}</span>
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${diffClass(idea.difficulty)}`}>{idea.difficulty}</span>
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-gray-100 text-[#374151]">{idea.opportunity_size}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {IDEA_ACTIONS.map((a) => (
                      <button
                        key={a.key}
                        onClick={() => a.fn(idea)}
                        data-testid={`scanner-idea-${i}-${a.key}`}
                        className={`inline-flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-bold border transition-colors ${
                          a.key === "blueprint" ? "col-span-2 bg-[#0F0F0F] text-white border-[#0F0F0F] hover:bg-black" : "bg-white text-[#374151] border-gray-200 hover:border-[#D4AF37]/60"
                        }`}
                      >
                        <a.icon size={13} /> {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hidden Opportunities + What's Next */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
            <div className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="scanner-hidden">
              <h4 className="font-display text-lg font-bold text-[#0F0F0F]">Hidden Opportunities</h4>
              <p className="text-sm text-[#687280] mt-1">Deeper angles the AI spotted in your image.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {result.hidden_opportunities.map((h, i) => {
                  const Icon = hiddenIcon(h.type);
                  return (
                    <div key={i} className="flex items-start gap-3 rounded-xl border border-gray-100 p-3.5" data-testid={`scanner-hidden-${i}`}>
                      <span className="w-9 h-9 rounded-xl bg-[#FBF7EA] text-[#8a6d1a] flex items-center justify-center shrink-0"><Icon size={17} /></span>
                      <div>
                        <p className="text-sm font-bold text-[#0F0F0F]">{h.type}</p>
                        <p className="text-xs text-[#687280] mt-0.5 leading-snug">{h.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-6" data-testid="scanner-whats-next">
              <h4 className="font-display text-lg font-bold">What's Next?</h4>
              <p className="text-sm text-[#c9c39f] mt-1">Keep building with LaunchBS tools.</p>
              <div className="mt-4 space-y-2">
                <button onClick={() => window.dispatchEvent(new CustomEvent("lbs:open-blueprint", { detail: { mode: "country", image } }))} className="w-full inline-flex items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm font-bold bg-white/10 hover:bg-white/20 transition-colors" data-testid="scanner-next-country">
                  <span className="inline-flex items-center gap-2"><Globe size={16} className="text-[#D4AF37]" /> AI Country Ideas</span> <ArrowRight size={15} />
                </button>
                <button onClick={() => navigate("/ai-resources/roadmap")} className="w-full inline-flex items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm font-bold bg-white/10 hover:bg-white/20 transition-colors" data-testid="scanner-next-roadmap">
                  <span className="inline-flex items-center gap-2"><Map size={16} className="text-[#D4AF37]" /> Founder Roadmap</span> <ArrowRight size={15} />
                </button>
                <button onClick={() => navigate("/resources/business-formation")} className="w-full inline-flex items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm font-bold bg-white/10 hover:bg-white/20 transition-colors" data-testid="scanner-next-formation">
                  <span className="inline-flex items-center gap-2"><Briefcase size={16} className="text-[#D4AF37]" /> Business Formation</span> <ArrowRight size={15} />
                </button>
                <button onClick={() => navigate("/notebook")} className="w-full inline-flex items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm font-bold bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030] transition-colors" data-testid="scanner-next-notebook">
                  <span className="inline-flex items-center gap-2"><Bookmark size={16} /> Startup Notebook</span> <ArrowRight size={15} />
                </button>
              </div>
              <p className="text-[11px] text-[#c9c39f] mt-4">Tap any action on an idea above to send it into these tools.</p>
            </div>
          </div>
        </div>
      )}
      </>)}
    </AIResourcesShell>
  );
}
